import React, { memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dimensions, Platform } from "react-native";

import {
  clearMessageNotifications,
  clearHomeNotifications,
  clearGeneralNotifications,
  clearResourceNotifications,
  getNotificationsRequest,
} from "@microdoing/shared/services/notification/actions";
import { changeSearchType } from "@microdoing/shared/services/search/actions";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import i18n from "@microdoing/shared/i18n";
import {
  getUserId,
  getUserFirstName,
  getUserPicture,
} from "@microdoing/shared/services/user/selectors";
import { getIsForbidden } from "@microdoing/shared/services/coaching/selectors";
import { getTabs } from "@microdoing/shared/services/profileSettings/selectors";

import { Box, MainText, PressableOpacity } from "app/components/common";
import normalize from "app/utils/normalize";
import { TabBarIcon } from "./IconTabBar";
import {
  getAchievement,
  getNewGeneralNotifications,
  getNewHomeNotifications,
  getNewMessageNotifications,
  getNewResourceNotifications,
} from "@microdoing/shared/services/notification/selectors";
import { getCurrentRoute, getTabNotifications } from "./utils";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const { width } = Dimensions.get("window");

interface ITab {
  index: number;
  name: string;
  tabBarTestID?: string;
  label?: string;
}

interface ITabBar {
  isForbidden: boolean;
  state: any;
  achievement: { name: string; level: number };
  language: string;
  userPicture: string;
  firstName: string;
  tabs: ITab[];
  isValidateRequesting: boolean;
  userId: string;
}

interface IRenderTab {
  userPicture: string;
  firstName: string;
  onTabPress: (tab: any) => void;
  isActive: boolean;
  routeName: string;
  label: string;
  notifications: number;
  tabBarTestID: string;
}

const getActiveRouteName = state => {
  const route = state.routes[state.index];

  if (route.state) {
    // Dive into nested navigators
    return getActiveRouteName(route.state);
  }

  return route.name;
};

const Tab = ({
  userPicture,
  firstName,
  onTabPress,
  isActive,
  label,
  routeName,
  notifications = 0,
  tabBarTestID,
}: IRenderTab) => {
  const insets = useSafeAreaInsets();

  const TAB_HEIGHT = useMemo(() => normalize(70) + insets.bottom / 2, []);

  return (
    <PressableOpacity
      testID={tabBarTestID}
      onPress={onTabPress}
      alignItems="center"
      justifyContent="flex-start"
      flex={1}
      height={TAB_HEIGHT}
      minHeight={TAB_HEIGHT}
      maxHeight={TAB_HEIGHT}
      backgroundColor="neutral.white"
      hitSlop={undefined}
      paddingTop="sm"
    >
      {TabBarIcon(routeName, userPicture, firstName)({ isActive })}
      <MainText
        marginTop={4}
        variant={isActive ? "l2-bold" : "l2-medium"}
        color={isActive ? "brand.500" : "neutral.600"}
      >
        {label}
      </MainText>
      {!!notifications && (
        <Box
          height={normalize(18)}
          width={normalize(18)}
          borderRadius={normalize(9)}
          borderWidth={normalize(1.5)}
          borderColor="neutral.white"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          backgroundColor="system.red.900"
          position="absolute"
          top={10}
          right={16}
        >
          <MainText variant="l2-medium" color="neutral.white">
            {notifications}
          </MainText>
        </Box>
      )}
    </PressableOpacity>
  );
};

const TabBar = ({ navigation, state, descriptors }: ITabBar) => {
  const newMessageNotifications = useSelector(getNewMessageNotifications);
  const newHomeNotifications = useSelector(getNewHomeNotifications);
  const newGeneralNotifications = useSelector(getNewGeneralNotifications);
  const newResourceNotifications = useSelector(getNewResourceNotifications);
  const isForbidden = useSelector(getIsForbidden);
  const firstName = useSelector(getUserFirstName);
  const userId = useSelector(getUserId);
  const tabs = useSelector(getTabs);
  const userPicture = useSelector(getUserPicture);

  const dispatch = useDispatch();

  const onTabPress = (newTab: ITab) => () => {
    const isFocused = state.index === newTab.index;
    const activeRouteName = getActiveRouteName(state);

    // Coaching finished - no longer access to his app
    if (isForbidden) {
      navigation.navigate("Coaching");
      return;
    }

    if (
      (newTab.name === "Message" && newMessageNotifications) ||
      (activeRouteName === "Message" && newMessageNotifications)
    ) {
      dispatch(clearMessageNotifications());
    }

    if (
      (newTab.name === "Library" && newResourceNotifications) ||
      (activeRouteName === "Library" && newResourceNotifications)
    ) {
      dispatch(clearResourceNotifications());
    }

    if (newTab.name === "Notification" && newGeneralNotifications) {
      dispatch(
        getNotificationsRequest({
          page: 0,
          userId: userId,
        }),
      );
      dispatch(clearGeneralNotifications());
    }
    if (activeRouteName === "Notification" && newGeneralNotifications) {
      dispatch(clearGeneralNotifications());
    }

    if (
      (newTab.name === "Home" && newHomeNotifications) ||
      (activeRouteName === "Home" && newHomeNotifications)
    ) {
      dispatch(clearHomeNotifications());
    }

    if (newTab.name === "SearchTab") {
      dispatch(changeSearchType("directory"));
    }

    if (
      newTab.name === "Home" &&
      isFocused &&
      activeRouteName !== "DailyFocusScreen" &&
      activeRouteName !== "Home"
    ) {
      return navigation.navigate("DailyFocusScreen");
    }
    const event = navigation.emit({
      type: "tabPress",
      target: newTab.name,
      canPreventDefault: true,
    });

    if (
      isFocused &&
      (activeRouteName === "UserProfile" ||
        activeRouteName === "Library" ||
        activeRouteName === "CampaignResources" ||
        activeRouteName === "CoachResources" ||
        activeRouteName === "Coaching" ||
        activeRouteName === "Notification" ||
        activeRouteName === "Progress" ||
        activeRouteName === "Home" ||
        activeRouteName === "DailyFocusScreen" ||
        activeRouteName === "Team")
    ) {
      dispatch(changeNavField({ scrollTop: true }));
    }

    if (!isFocused && !event.defaultPrevented) {
      navigation.navigate(newTab.name);
    }
  };

  return (
    <Box
      flexDirection="row"
      width={width}
      zIndex={10}
      shadowColor="neutral.white"
      borderTopWidth={1}
      borderTopColor="neutral.transparent.white.80"
    >
      {tabs.map(tab => {
        const { label, name, index, tabBarTestID } = tab;
        const isFocused = state.index === tab.index;

        return (
          <Tab
            key={name}
            tabBarTestID={tabBarTestID}
            userPicture={userPicture}
            firstName={firstName}
            isActive={isFocused}
            onTabPress={onTabPress({
              name: name,
              index: index,
            })}
            label={i18n.t(label)}
            routeName={name}
            notifications={getTabNotifications({
              isActive: isFocused,
              tabName: name,
              newGeneralNotifications,
              newHomeNotifications,
              newMessageNotifications,
              newResourceNotifications,
            })}
          />
        );
      })}
    </Box>
  );
};

export default memo(TabBar);
